import { ComboBox, Input, Label, ListBox } from "@heroui/react";
import type { ReactNode } from "react";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { clsx } from "@/src/common/utils/clsx";

interface ComboBoxGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>, TOption extends string> {
	control: Control<TFieldValues>;
	name: TName;
	label?: ReactNode;
	placeholder?: string;
	options: readonly TOption[];
	getOptionLabel?: (option: TOption) => string;
	className?: string;
	onCommit?: () => void;
}

/**
 * RHF-aware combo box for nullable single-select string fields: label +
 * filterable input + option list. Selecting an option commits its id;
 * clearing the input commits null (the form-level blur flush persists it).
 */
export function ComboBoxGroup<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>, TOption extends string>({
	control,
	name,
	label,
	placeholder,
	options,
	getOptionLabel,
	className,
	onCommit,
}: ComboBoxGroupProps<TFieldValues, TName, TOption>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => (
				<ComboBox
					className={clsx("flex w-full flex-col gap-1.5", className)}
					selectedKey={(field.value as string | null) ?? null}
					onSelectionChange={(key) => {
						field.onChange(key != null ? String(key) : null);
						onCommit?.();
					}}
					onInputChange={(value) => {
						if (value === "") field.onChange(null);
					}}
				>
					{label ? <Label>{label}</Label> : null}
					<ComboBox.InputGroup>
						<Input placeholder={placeholder} />
						<ComboBox.Trigger />
					</ComboBox.InputGroup>
					<ComboBox.Popover className="w-(--trigger-width)">
						<ListBox>
							{options.map((option) => {
								const optionLabel = getOptionLabel?.(option) ?? option;
								return (
									<ListBox.Item key={option} id={option} textValue={optionLabel}>
										<span className="block truncate">{optionLabel}</span>
										<ListBox.ItemIndicator />
									</ListBox.Item>
								);
							})}
						</ListBox>
					</ComboBox.Popover>
				</ComboBox>
			)}
		/>
	);
}
